import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { ChevronDown, ChevronUp, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function PicksWatchdog({ group }) {
  const [dates, setDates] = useState([]);
  const [activeDate, setActiveDate] = useState(null);
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState({});

  const loadDates = useCallback(async () => {
    if (!group) return;
    try {
      const { data } = await api.get(`/groups/${group.id}/meeting-dates`);
      setDates(data.meeting_dates);
      setActiveDate((cur) => {
        if (cur && data.meeting_dates.find((d) => d.meeting_date === cur)) return cur;
        const upcoming = data.meeting_dates.find((d) => !d.cutoff_passed);
        return (upcoming || data.meeting_dates[data.meeting_dates.length - 1])?.meeting_date || null;
      });
    } catch (_) {}
  }, [group]);

  const loadStatus = useCallback(async () => {
    if (!group || !activeDate) return;
    try {
      const { data } = await api.get(`/groups/${group.id}/picks-status`, { params: { date: activeDate } });
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Watchdog load failed"));
    }
  }, [group, activeDate]);

  useEffect(() => { loadDates(); }, [loadDates]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  if (!group) return null;
  if (dates.length === 0) {
    return (
      <div className="bg-white border border-[#e9ecef] rounded-2xl p-4 text-center text-sm text-[#6c757d]" data-testid="picks-watchdog-empty">
        Load a race card first to see picks status.
      </div>
    );
  }

  const buckets = status?.buckets || { confirmed: [], staged_only: [], no_picks: [] };
  const rows = [
    { key: "confirmed", label: "Confirmed", icon: CheckCircle2, colour: "#2f9e44", entries: buckets.confirmed, hint: "In the ladder" },
    { key: "staged_only", label: "Staged only (not confirmed)", icon: Clock, colour: "#c07800", entries: buckets.staged_only, hint: "Chase them up" },
    { key: "no_picks", label: "No picks yet", icon: AlertCircle, colour: "#c92a2a", entries: buckets.no_picks, hint: "Nudge them" },
  ];

  return (
    <div className="bg-white border border-[#e9ecef] rounded-2xl p-4 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]" data-testid="picks-watchdog">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-display font-extrabold text-base">Picks status</h3>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#6c757d]">
            {group.name} · {activeDate} · {status?.n_races || 0} races
          </div>
        </div>
        <select
          value={activeDate || ""}
          onChange={(e) => setActiveDate(e.target.value)}
          className="rounded-lg border border-[#e9ecef] text-xs font-bold py-1 px-2 bg-white"
          data-testid="watchdog-date-select"
        >
          {dates.map((d) => (
            <option key={d.meeting_date} value={d.meeting_date}>{d.meeting_date}</option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        {rows.map((row) => {
          const Icon = row.icon;
          const n = row.entries.length;
          const isOpen = expanded[row.key];
          return (
            <div key={row.key} className="border border-[#e9ecef] rounded-xl overflow-hidden" data-testid={`watchdog-bucket-${row.key}`}>
              <button
                onClick={() => setExpanded((p) => ({ ...p, [row.key]: !p[row.key] }))}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#f8f9fa] transition-colors text-left"
                data-testid={`watchdog-bucket-${row.key}-toggle`}
              >
                <Icon className="w-5 h-5 shrink-0" strokeWidth={2.5} style={{ color: row.colour }} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-extrabold text-sm">
                    {row.label}: <span className="tabular-nums">{n}</span>
                  </div>
                  <div className="text-[10px] text-[#6c757d] font-bold">{row.hint}</div>
                </div>
                {n > 0 && (isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
              </button>
              {isOpen && n > 0 && (
                <div className="px-3 pb-3 pt-1 space-y-1.5 bg-[#f8f9fa]" data-testid={`watchdog-bucket-${row.key}-list`}>
                  {row.entries.map((e) => (
                    <div key={e.user_id} className="flex items-center gap-2 bg-white border border-[#e9ecef] rounded-lg px-2.5 py-2">
                      <span className="text-lg">{e.avatar_emoji}</span>
                      <span className="flex-1 text-sm font-bold truncate">{e.display_name}</span>
                      <span className="text-[10px] font-bold text-[#6c757d]">
                        {e.confirmed_count > 0 && <>{e.confirmed_count} confirmed · </>}
                        {e.staged_count > 0 && <>{e.staged_count} staged · </>}
                        {e.picks_count === 0 && "0 picks"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
